import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import './CustomSearch.scss';

const CustomSearch = ({
  onSelect,
  onSearch,
  placeholder = 'Search',
  className = '',
  debounceMs = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState('left');
  const [dropdownWidth, setDropdownWidth] = useState(250);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const resultRefs = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      calculateDropdownDimensions();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, filteredResults]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset refs array when results change
  useEffect(() => {
    resultRefs.current = resultRefs.current.slice(0, filteredResults.length);
  }, [filteredResults]);

  // Scroll highlighted item into view when navigating with arrow keys
  useEffect(() => {
    if (highlightedIndex >= 0 && resultRefs.current[highlightedIndex]) {
      resultRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  const calculateDropdownDimensions = () => {
    if (!inputRef.current || filteredResults.length === 0) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const inputWidth = inputRect.width;

    let maxContentWidth = inputWidth;

    filteredResults.forEach(patient => {
      const idLength = (patient.custom_id || '').length;
      const emailLength = (patient.email || '').length;

      const estimatedIdWidth = idLength * 8 + 32;
      const estimatedEmailWidth = emailLength * 7 + 32;

      const contentWidth = Math.max(estimatedIdWidth, estimatedEmailWidth);
      maxContentWidth = Math.max(maxContentWidth, contentWidth);
    });

    const maxAllowedWidth = Math.min(maxContentWidth, 450);
    setDropdownWidth(maxAllowedWidth);

    const spaceOnRight = viewportWidth - inputRect.left;
    const spaceOnLeft = inputRect.right;

    if (maxAllowedWidth <= spaceOnRight) {
      setDropdownPosition('left');
    } else if (maxAllowedWidth <= spaceOnLeft) {
      setDropdownPosition('right');
    } else {
      setDropdownPosition('right');
    }
  };

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setFilteredResults([]);
      setIsOpen(false);
      return;
    }

    if (!onSearch) {
      console.error('CustomSearch: onSearch callback is required');
      setFilteredResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await onSearch(query);
      setFilteredResults(results.slice(0, 25));
      setIsOpen(results.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setFilteredResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [onSearch]);

  // Returns React elements instead of HTML strings to prevent XSS
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));

    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i}>{part}</mark>
        : part
    );
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    setHighlightedIndex(-1);

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the search
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, debounceMs);
  };

  const handleInputFocus = () => {
    if (searchValue.trim() && filteredResults.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectItem(filteredResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        // No action needed for other keys
        break;
    }
  };

  const handleSelectItem = (item) => {
    onSelect(item);
    setSearchValue('');
    setFilteredResults([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setSearchValue('');
    setFilteredResults([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const renderResult = (patient, index) => {
    const isHighlighted = index === highlightedIndex;

    return (
      <div
        key={patient.custom_id}
        ref={el => resultRefs.current[index] = el}
        className={`search-result ${isHighlighted ? 'highlighted' : ''}`}
        onClick={() => handleSelectItem(patient)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        <div className="patient-info">
          <div className="custom-patient-id">
            {highlightMatch(patient.custom_id, searchValue)}
          </div>
          <div className="patient-email">
            {highlightMatch(patient.email, searchValue)}
          </div>
          <div className="patient-study">
            {patient.shoulder_arthroplasty && <span className="study-badge">SAp</span>}
            {patient.shoulder_arthroscopy && <span className="study-badge">SAs</span>}
            {patient.elbow && <span className="study-badge">E</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`custom-search ${className}`} ref={dropdownRef}>
      <div className="search-input-container" ref={inputRef}>
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="search-input"
            autoComplete="off"
          />
          {searchValue && (
            <FaTimes
              className="clear-icon"
              onClick={handleClear}
            />
          )}
        </div>
      </div>

      {isOpen && (filteredResults.length > 0 || isLoading) && (
        <div
          className={`search-dropdown ${dropdownPosition === 'right' ? 'align-right' : dropdownPosition === 'center' ? 'align-center' : ''}`}
          style={{ width: `${dropdownWidth}px` }}
        >
          {isLoading ? (
            <div className="search-loading">Searching...</div>
          ) : (
            <>
              <div className="search-results">
                {filteredResults.map((patient, index) => renderResult(patient, index))}
              </div>
              {filteredResults.length >= 25 && (
                <div className="search-footer">
                  Showing first 25 results. Refine your search for more specific results.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSearch;
